import { hooks } from '@approvalmax/utils';
import { State } from 'modules/data';
import { hooks as pageHooks } from 'modules/page';
import { useSelector } from 'modules/react-redux';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useMount } from 'react-use';

import { loadOneRequest, loadPageData, setActiveRequestId, showRequestSearchPopup } from '../actions';
import { SEARCH_REQUESTS_LIST_PAGE_ID } from '../config';
import { createEmptySearchParams } from '../data/SearchParameters';
import { checkRequestLoaded } from '../selectors/pageSelectors';

const useSearchRequestsListPageData = () => {
    const dispatch = useDispatch();
    const [requestIdParam] = hooks.useQueryParam('requestId');
    const { companyId } = useParams<{ companyId: string }>();
    const isRequestLoaded = useSelector((state: State) => checkRequestLoaded(state, requestIdParam));
    const noCompanies = pageHooks.useNoCompanies();

    const initialRequestParams = useRef(requestIdParam && companyId ? { requestId: requestIdParam, companyId } : null);

    const defaultLoadPageData = useCallback(() => {
        dispatch(
            loadPageData({
                filter: null,
                companyId: null,
                templateId: null,
                requestId: null,
                requests: [],
                hasMore: false,
                entities: {},
                searchParameters: createEmptySearchParams(),
                innerPageId: SEARCH_REQUESTS_LIST_PAGE_ID,
                initLoad: true,
            })
        );
    }, [dispatch]);

    const loadInitialRequest = useCallback(async () => {
        if (!initialRequestParams.current) {
            return;
        }

        try {
            const result = await loadOneRequest(
                initialRequestParams.current.requestId,
                initialRequestParams.current.companyId
            );

            dispatch(
                loadPageData({
                    filter: null,
                    companyId: null,
                    templateId: null,
                    requestId: result?.request.id ?? null,
                    requests: result?.request.id ? [result.request.id] : [],
                    hasMore: false,
                    entities: result?.entities ?? {},
                    searchParameters: createEmptySearchParams(),
                    innerPageId: SEARCH_REQUESTS_LIST_PAGE_ID,
                    initLoad: true,
                })
            );

            if (!result) {
                dispatch(showRequestSearchPopup());
            }
        } catch {
            defaultLoadPageData();

            dispatch(showRequestSearchPopup());
        }
    }, [dispatch, defaultLoadPageData]);

    useEffect(() => {
        if (noCompanies) {
            return;
        }

        if (initialRequestParams.current) {
            loadInitialRequest();
        } else {
            defaultLoadPageData();
        }
    }, [dispatch, noCompanies, defaultLoadPageData, loadInitialRequest]);

    useMount(() => {
        if (!initialRequestParams.current) {
            dispatch(showRequestSearchPopup());
        }
    });

    useEffect(() => {
        if (isRequestLoaded) {
            dispatch(setActiveRequestId(requestIdParam));
        }
    }, [requestIdParam, dispatch, isRequestLoaded]);
};

export default useSearchRequestsListPageData;
